import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';

export default function ClassRecords({ user }) {
  const [records, setRecords] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(''); // 'compressing', 'uploading', 'processing'
  const [videoUrls, setVideoUrls] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  
  // Helper function to get today's date in local timezone
  const getTodayLocalDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const result = `${year}-${month}-${day}`;
    console.log('🗓️ getTodayLocalDate called:', {
      date: today,
      year,
      month,
      day,
      result,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    return result;
  };
  
  const [formData, setFormData] = useState({
    date: getTodayLocalDate(),
    time: '',
    location_id: '',
    notes: ''
  });
  const [editingRecord, setEditingRecord] = useState(null);
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadRecords();
    loadLocations();
  }, []);

  // Reset form data when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      if (editingRecord) {
        // Editing mode - populate with existing data
        setFormData({
          date: editingRecord.date,
          time: editingRecord.time || '',
          location_id: editingRecord.location_id,
          notes: editingRecord.notes || ''
        });
        setVideoUrls(editingRecord.video_urls || []);
      } else {
        // Adding mode - reset to defaults
        setFormData({
          date: getTodayLocalDate(),
          time: '',
          location_id: '',
          notes: ''
        });
        setVideoUrls([]);
      }
    }
  }, [dialogOpen, editingRecord]);

  const loadRecords = async () => {
    try {
      const res = await axios.get(`${API}/class-records`);
      // Sort records by date in descending order (newest first)
      const sortedRecords = res.data.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });
      setRecords(sortedRecords);
    } catch (error) {
      toast.error('載入課堂紀錄失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch (error) {
      console.error('Failed to load locations', error);
    }
  };

  // Compress video using canvas and MediaRecorder
  const compressVideo = async (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size (compress to max 720p)
        const maxWidth = 1280;
        const maxHeight = 720;
        let width = video.videoWidth;
        let height = video.videoHeight;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Create MediaRecorder for compression
        const stream = canvas.captureStream(30); // 30 fps
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'video/webm;codecs=vp8',
          videoBitsPerSecond: 1000000 // 1 Mbps
        });
        
        const chunks = [];
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };
        
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          resolve(blob);
        };
        
        mediaRecorder.start();
        video.play();
        
        const drawFrame = () => {
          if (!video.paused && !video.ended) {
            ctx.drawImage(video, 0, 0, width, height);
            requestAnimationFrame(drawFrame);
          } else {
            mediaRecorder.stop();
          }
        };
        
        drawFrame();
      };
      
      video.onerror = () => {
        reject(new Error('Failed to load video'));
      };
      
      video.src = URL.createObjectURL(file);
    });
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      toast.error('視頻文件過大，請選擇小於100MB的文件');
      return;
    }
    
    setUploadingVideo(true);
    setUploadProgress(0);
    setUploadStatus('compressing');
    toast.info('📹 開始處理視頻...');
    
    try {
      // Compress video
      let fileToUpload = file;
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      
      if (file.size > 10 * 1024 * 1024) { // If larger than 10MB, compress
        setUploadStatus('compressing');
        toast.info(`🔄 正在壓縮視頻 (${fileSizeMB}MB)...`);
        try {
          fileToUpload = await compressVideo(file);
          const compressedSizeMB = (fileToUpload.size / (1024 * 1024)).toFixed(2);
          setUploadProgress(30);
          toast.success(`✅ 壓縮完成！${fileSizeMB}MB → ${compressedSizeMB}MB`);
        } catch (error) {
          toast.warning('⚠️ 壓縮失敗，將上傳原始文件');
          fileToUpload = file;
        }
      } else {
        setUploadProgress(30);
      }
      
      const formData = new FormData();
      formData.append('file', fileToUpload, 'video.webm');
      
      setUploadStatus('uploading');
      const uploadSizeMB = (fileToUpload.size / (1024 * 1024)).toFixed(2);
      toast.info(`⬆️ 正在上傳視頻 (${uploadSizeMB}MB)...`);
      
      const res = await axios.post(`${API}/class-records/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 70) / progressEvent.total) + 30;
          setUploadProgress(percentCompleted);
        }
      });
      
      setUploadProgress(100);
      setUploadStatus('processing');
      setVideoUrls([...videoUrls, res.data.video_url]);
      toast.success('🎉 視頻上傳成功！');
    } catch (error) {
      toast.error('❌ 視頻上傳失敗，請重試');
      console.error(error);
    } finally {
      // Reset after a short delay to show 100% completion
      setTimeout(() => {
        setUploadingVideo(false);
        setUploadProgress(0);
        setUploadStatus('');
      }, 1000);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const startRecording = async () => {
    try {
      // Check if mediaDevices is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('您的瀏覽器不支持相機功能，請使用現代瀏覽器（Chrome、Firefox、Safari等）');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 1280, height: 720 }, 
        audio: true 
      });
      
      videoRef.current.srcObject = stream;
      videoRef.current.play();
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8',
        videoBitsPerSecond: 1000000 // 1 Mbps for compression
      });
      
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        await uploadRecordedVideo(blob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      toast.success('開始錄製');
    } catch (error) {
      console.error('相機錯誤:', error);
      
      if (error.name === 'NotAllowedError') {
        toast.error('您拒絕了相機權限。請在瀏覽器設置中允許此網站訪問相機和麥克風。');
      } else if (error.name === 'NotFoundError') {
        toast.error('未找到相機或麥克風設備。請確保設備已連接。');
      } else if (error.name === 'NotReadableError') {
        toast.error('無法訪問相機，可能被其他應用程序佔用。');
      } else if (error.name === 'SecurityError') {
        toast.error('相機訪問被阻止。請確保網站使用 HTTPS 連接。');
      } else {
        toast.error('無法訪問相機或麥克風。錯誤: ' + error.message);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.info('正在處理視頻...');
    }
  };

  const uploadRecordedVideo = async (blob) => {
    setUploadingVideo(true);
    setUploadProgress(0);
    setUploadStatus('uploading');
    
    try {
      const formData = new FormData();
      formData.append('file', blob, 'recorded-video.webm');
      
      const sizeMB = (blob.size / (1024 * 1024)).toFixed(2);
      toast.info(`⬆️ 正在上傳錄製的視頻 (${sizeMB}MB)...`);
      
      const res = await axios.post(`${API}/class-records/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      setUploadProgress(100);
      setVideoUrls([...videoUrls, res.data.video_url]);
      toast.success('🎉 錄製視頻上傳成功！');
    } catch (error) {
      toast.error('❌ 視頻上傳失敗，請重試');
      console.error(error);
    } finally {
      setTimeout(() => {
        setUploadingVideo(false);
        setUploadProgress(0);
        setUploadStatus('');
      }, 1000);
    }
  };

  const removeVideo = (index) => {
    setVideoUrls(videoUrls.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.location_id) {
      toast.error('請選擇上課地點');
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('date', formData.date);
      submitData.append('time', formData.time || '');
      submitData.append('location_id', formData.location_id);
      submitData.append('notes', formData.notes || '');
      submitData.append('video_urls', JSON.stringify(videoUrls));
      
      if (editingRecord) {
        // Update existing record
        await axios.put(`${API}/class-records/${editingRecord.record_id}`, submitData);
        toast.success('✅ 課堂紀錄已更新');
      } else {
        // Create new record
        await axios.post(`${API}/class-records`, submitData);
        toast.success('✅ 課堂紀錄已新增');
      }
      
      setDialogOpen(false);
      setEditingRecord(null);
      setVideoUrls([]);
      loadRecords();
    } catch (error) {
      toast.error(editingRecord ? '更新失敗' : '新增失敗');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setDialogOpen(true);
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('確定要刪除這筆紀錄嗎？')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/class-records/${recordId}`);
      toast.success('✅ 紀錄已刪除');
      loadRecords();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  return (
    <div className="p-4 md:p-8 pt-20 md:pt-8" data-testid="class-records-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-gray-800">課堂紀錄</h1>
          
          {user.role === 'student' && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-record-btn" className="bg-orange-500 hover:bg-orange-600">
                  新增紀錄
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingRecord ? '編輯課堂紀錄' : '新增課堂紀錄'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">日期</label>
                    <Input
                      data-testid="record-date-input"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      max={getTodayLocalDate()}
                    />
                    {/* Quick date selection buttons */}
                    {!editingRecord && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({...formData, date: getTodayLocalDate()});
                        }}
                        className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full transition-colors"
                      >
                        今天
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const yesterday = new Date();
                          yesterday.setDate(yesterday.getDate() - 1);
                          const year = yesterday.getFullYear();
                          const month = String(yesterday.getMonth() + 1).padStart(2, '0');
                          const day = String(yesterday.getDate()).padStart(2, '0');
                          setFormData({...formData, date: `${year}-${month}-${day}`});
                        }}
                        className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
                      >
                        昨天
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const dayBefore = new Date();
                          dayBefore.setDate(dayBefore.getDate() - 2);
                          const year = dayBefore.getFullYear();
                          const month = String(dayBefore.getMonth() + 1).padStart(2, '0');
                          const day = String(dayBefore.getDate()).padStart(2, '0');
                          setFormData({...formData, date: `${year}-${month}-${day}`});
                        }}
                        className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
                      >
                        前天
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const lastWeek = new Date();
                          lastWeek.setDate(lastWeek.getDate() - 7);
                          const year = lastWeek.getFullYear();
                          const month = String(lastWeek.getMonth() + 1).padStart(2, '0');
                          const day = String(lastWeek.getDate()).padStart(2, '0');
                          setFormData({...formData, date: `${year}-${month}-${day}`});
                        }}
                        className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
                      >
                        上週今天
                      </button>
                    </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">時間（選填）</label>
                    <Input
                      data-testid="record-time-input"
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      placeholder="例如：14:30"
                    />
                    <p className="text-xs text-gray-500 mt-1">記錄課堂的具體時間</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">上課地點</label>
                    <Select value={formData.location_id} onValueChange={(value) => setFormData({...formData, location_id: value})}>
                      <SelectTrigger data-testid="location-select">
                        <SelectValue placeholder="選擇地點" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map(loc => (
                          <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">訓練視頻</label>
                    
                    {/* Video preview area */}
                    {isRecording && (
                      <div className="mb-4">
                        <video 
                          ref={videoRef} 
                          className="w-full rounded-lg border-2 border-red-500"
                          muted
                        />
                      </div>
                    )}
                    
                    {uploadingVideo && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-blue-900">
                            {uploadStatus === 'compressing' && '🔄 壓縮中...'}
                            {uploadStatus === 'uploading' && '⬆️ 上傳中...'}
                            {uploadStatus === 'processing' && '✨ 處理中...'}
                          </span>
                          <span className="text-sm font-bold text-blue-900">{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-full rounded-full transition-all duration-300 ease-out relative"
                            style={{ width: `${uploadProgress}%` }}
                          >
                            <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                          </div>
                        </div>
                        <p className="text-xs text-blue-700 mt-2">
                          {uploadProgress < 30 && '預估剩餘時間：計算中...'}
                          {uploadProgress >= 30 && uploadProgress < 70 && `預估剩餘時間：${Math.ceil((100 - uploadProgress) / 10)}秒`}
                          {uploadProgress >= 70 && uploadProgress < 100 && '即將完成...'}
                          {uploadProgress === 100 && '上傳完成！'}
                        </p>
                      </div>
                    )}
                    
                    {/* Video controls */}
                    <div className="flex gap-2 mb-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingVideo || isRecording}
                        className="flex-1"
                        variant="outline"
                      >
                        📤 上傳視頻
                      </Button>
                      
                      {!isRecording ? (
                        <Button
                          type="button"
                          onClick={startRecording}
                          disabled={uploadingVideo}
                          className="flex-1 bg-red-500 hover:bg-red-600"
                        >
                          🎥 開始拍攝
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={stopRecording}
                          className="flex-1 bg-gray-600 hover:bg-gray-700"
                        >
                          ⏹️ 停止拍攝
                        </Button>
                      )}
                    </div>
                    
                    {/* Uploaded videos list */}
                    {videoUrls.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">已上傳視頻 ({videoUrls.length}):</p>
                        {videoUrls.map((url, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                            <span className="text-sm text-gray-700">視頻 {index + 1}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              onClick={() => removeVideo(index)}
                            >
                              刪除
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {uploadingVideo && (
                      <p className="text-sm text-blue-600 mt-2">處理中，請稍候...</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">備註</label>
                    <Textarea
                      data-testid="notes-input"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="今天的訓練心得..."
                    />
                  </div>
                  
                  <Button 
                    data-testid="submit-record-btn" 
                    type="submit" 
                    className="w-full"
                    disabled={uploadingVideo || isRecording}
                  >
                    {editingRecord ? '更新紀錄' : '提交'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">載入中...</div>
          ) : records.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">暫無課堂紀錄</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {records.map((record) => {
                const location = locations.find(l => l.location_id === record.location_id);
                return (
                  <div 
                    key={record.record_id} 
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden" 
                    data-testid="record-item"
                  >
                    {/* 卡片頭部 */}
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="font-semibold text-lg">
                            {new Date(record.date).toLocaleDateString('zh-TW', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        {record.video_urls && record.video_urls.length > 0 && (
                          <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                            {record.video_urls.length} 個影片
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        {record.time && (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="opacity-90 mr-3">{record.time}</span>
                          </>
                        )}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="opacity-90">{location?.name || '未知地點'}</span>
                      </div>
                    </div>
                    
                    {/* 卡片內容 */}
                    <div className="p-4">
                      {record.notes && (
                        <div className="mb-3">
                          <p className="text-sm text-gray-600 line-clamp-2">{record.notes}</p>
                        </div>
                      )}
                      
                      {/* 影片縮圖 */}
                      {record.video_urls && record.video_urls.length > 0 && (
                        <div className="space-y-2">
                          {record.video_urls.slice(0, 1).map((videoUrl, index) => (
                            <div key={index} className="relative rounded-lg overflow-hidden bg-black flex items-center justify-center" style={{ minHeight: '240px', maxHeight: '400px' }}>
                              <video 
                                controls 
                                className="w-full h-auto max-h-[400px]"
                                style={{ objectFit: 'contain' }}
                                src={videoUrl.startsWith('http') ? videoUrl : `${window.location.origin}/api/media-files${videoUrl.replace('/media', '')}`}
                              >
                                您的瀏覽器不支持視頻播放
                              </video>
                            </div>
                          ))}
                          {record.video_urls.length > 1 && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <button className="w-full text-center text-sm text-orange-600 hover:text-orange-700 font-medium py-2 hover:bg-orange-50 rounded transition-colors">
                                  查看全部 {record.video_urls.length} 個影片 →
                                </button>
                              </DialogTrigger>
                              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>
                                    課堂影片 - {new Date(record.date).toLocaleDateString('zh-TW')}
                                  </DialogTitle>
                                </DialogHeader>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                  {record.video_urls.map((videoUrl, idx) => (
                                    <div key={idx} className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '200px' }}>
                                      <video 
                                        controls 
                                        className="w-full h-auto max-h-[400px]"
                                        style={{ objectFit: 'contain' }}
                                        src={videoUrl.startsWith('http') ? videoUrl : `${window.location.origin}/api/media-files${videoUrl.replace('/media', '')}`}
                                      />
                                      <p className="text-sm text-gray-600 mt-2 absolute bottom-2 left-2 bg-white/80 px-2 py-1 rounded">影片 {idx + 1}</p>
                                    </div>
                                  ))}
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      )}
                      
                      {/* 編輯和刪除按鈕 - 只有學生本人可見 */}
                      {user.role === 'student' && (
                        <div className="flex gap-2 mt-4 pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleEdit(record)}
                          >
                            ✏️ 編輯
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(record.record_id)}
                          >
                            🗑️ 刪除
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
